/**
 * API fetcher function for SWR
 * Handles authentication via session cookies and API response format
 */

interface ApiSuccessResponse<T> {
  data: T
}

interface ApiErrorResponse {
  error: string
}

type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse

/**
 * Fetcher function for SWR that handles the API response format
 * @param url - API endpoint URL
 * @returns Typed data from the API response
 * @throws Error if the request fails or returns an error response
 */
export async function fetcher<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    credentials: "include", // Include session cookies for authentication
    headers: {
      "Content-Type": "application/json",
    },
  })

  // Parse JSON response
  const json: ApiResponse<T> = await response.json()

  // Handle non-OK HTTP status
  if (!response.ok) {
    // Try to extract error message from response body
    if ("error" in json) {
      throw new Error(json.error)
    }
    // Fallback to generic HTTP error
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  // Check for error in response body
  if ("error" in json) {
    throw new Error(json.error)
  }

  // Check for data in response body
  if ("data" in json) {
    return json.data
  }

  // Handle malformed response (neither data nor error)
  throw new Error("No data or error in response")
}
